import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BookmarkTypes,
  IBookmarkCompanyCreatePayload,
  IBookmarkCompareCreatePayload,
  IBookmarkPeopleCreatePayload,
  IBookmarkTradeDataCreatePayload,
} from 'src/model/bookmark';
import BookmarkService from 'src/services/api/bookmark';
import { useNotification } from 'src/hooks/useNotification';
import { QUERY_KEY_GET_BOOKMARK } from 'src/services/queries/useQueryBookmark';

export const useMutateCreateBookmark = () => {
  const { notification } = useNotification();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: {
      type: BookmarkTypes;
      body:
        | IBookmarkCompanyCreatePayload
        | IBookmarkPeopleCreatePayload
        | IBookmarkTradeDataCreatePayload
        | IBookmarkCompareCreatePayload;
    }) => BookmarkService.createBookmark(payload.type, payload.body),
    onSettled: (response) => {
      if (!response) return;
      notification({ ...response });
    },
  });

  return {
    isPending,
    mutateAsync,
  };
};

export const useMutateDeleteBookmark = () => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: { type: BookmarkTypes; bookmarkId: string }) =>
      BookmarkService.deleteBookmark(payload.type, payload.bookmarkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_GET_BOOKMARK] });
    },
  });

  return {
    isPending,
    mutateAsync,
  };
};

export const useMutatePutBookmark = () => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: {
      type: BookmarkTypes;
      bookmarkId: string;
      body?: {
        note?: string;
      };
    }) => BookmarkService.putBookmark(payload.type, payload.bookmarkId, payload.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_GET_BOOKMARK] });
    },
  });

  return {
    isPending,
    mutateAsync,
  };
};

export const useMutateUpdateBookmarkByDataUuid = () => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: {
      type: BookmarkTypes;
      dataUuid: string;
      body?: {
        note?: string;
      };
    }) => BookmarkService.updateBookmarkByDataUuid(payload.type, payload.dataUuid, payload.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_GET_BOOKMARK] });
    },
  });

  return {
    isPending,
    mutateAsync,
  };
};

export const useMutateDeleteBookmarkData = () => {
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: { type: BookmarkTypes; dataUuid: string }) =>
      BookmarkService.deleteBookmarkData(payload.type, payload.dataUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_GET_BOOKMARK] });
    },
  });

  return {
    isPending,
    mutateAsync,
  };
};
